/** Cursor states from the `data-cursor` contract, plus `default`. */
export type CursorState = 'default' | 'link' | 'view' | 'drag' | 'text' | 'hide'

/** What the cursor should look like over an element. */
export interface CursorTarget {
  state: CursorState
  /** Pill label for `view` / `drag` (empty otherwise). */
  label: string
  /** The `data-theme` of the section under the pointer. */
  theme: string
}

const DECLARED = new Set<string>(['default', 'link', 'view', 'drag', 'text', 'hide'])

/** Text fields keep the native I-beam, so the custom cursor steps aside over them. */
export const FIELD_SELECTOR = [
  'textarea',
  'select',
  '[contenteditable=""]',
  '[contenteditable="true"]',
  'input:not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([type="color"]):not([type="file"])',
].join(', ')

/** Interactive elements that never declared a state still get the `link` morph. */
const INTERACTIVE_SELECTOR = [
  'a[href]',
  'button:not(:disabled)',
  '[role="button"]',
  '[role="link"]',
  'summary',
  'label[for]',
  'input[type="checkbox"]',
  'input[type="radio"]',
  'input[type="range"]',
  'input[type="submit"]',
  'input[type="button"]',
].join(', ')

/**
 * Labels for `view` and `drag` when the element gives none: the state name itself
 * ("view" → "View"), so no copy is invented here.
 */
function defaultLabel(state: CursorState): string {
  return state === 'view' || state === 'drag' ? state[0].toUpperCase() + state.slice(1) : ''
}

/**
 * Resolves the cursor for the element under the pointer. The nearest `data-cursor` wins;
 * text fields fall back to the native cursor; other interactive elements become `link`.
 */
export function resolveCursor(target: Element | null): CursorTarget {
  const theme = target?.closest<HTMLElement>('[data-theme]')?.dataset.theme ?? 'light'
  if (!target) return { state: 'default', label: '', theme }

  const declared = target.closest<HTMLElement>('[data-cursor]')
  const field = target.closest(FIELD_SELECTOR)
  // A field inside a declared region still shows the native caret.
  if (field && (!declared || (declared !== field && declared.contains(field)))) {
    return { state: 'hide', label: '', theme }
  }
  if (declared) {
    const value = declared.dataset.cursor ?? ''
    const state: CursorState = DECLARED.has(value) ? (value as CursorState) : 'link'
    return { state, label: declared.dataset.cursorLabel ?? defaultLabel(state), theme }
  }
  if (target.closest(INTERACTIVE_SELECTOR)) return { state: 'link', label: '', theme }
  return { state: 'default', label: '', theme }
}
